import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, Music, Users } from 'lucide-react';
import { naatKhawanService, audioService } from '../services/api';
import { NaatKhawan, AudioFile } from '../types';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';
import NaatCard from '../components/NaatCard';
import SEO from '../components/SEO';
import './SearchResultsPage.css';
import './NaatKhawansPage.css';

const useQuery = () => new URLSearchParams(useLocation().search);

const SearchResultsPage: React.FC = () => {
  const query = useQuery();
  const q = query.get('q') || '';
  const { state, playAudio, addToQueue, togglePlay } = useAudioPlayer();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [naatKhawans, setNaatKhawans] = useState<NaatKhawan[]>([]);
  const [audios, setAudios] = useState<AudioFile[]>([]);
  const [nkPage, setNkPage] = useState(1);
  const [audioPage, setAudioPage] = useState(1);
  const [nkTotalPages, setNkTotalPages] = useState(1);
  const [audioTotalPages, setAudioTotalPages] = useState(1);
  const [nkTotalCount, setNkTotalCount] = useState(0);
  const [audioTotalCount, setAudioTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Fetch when query or page changes
  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      if (!q.trim()) return;
      setLoading(true);
      setError(null);
      try {
        const [nkResp, audioResp] = await Promise.all([
          naatKhawanService.getAll(nkPage, 20, q.trim()),
          audioService.getAll(audioPage, 20, q.trim())
        ]);
        if (!ignore && nkResp.success) {
          setNkTotalPages(nkResp.data.totalPages);
          setNkTotalCount(nkResp.data.total || 0);
          setNaatKhawans(prev => nkPage === 1 ? (nkResp.data.data || []) : [...prev, ...(nkResp.data.data || [])]);
        }
        if (!ignore && audioResp.success) {
          setAudioTotalPages(audioResp.data.totalPages);
          setAudioTotalCount(audioResp.data.total || 0);
          setAudios(prev => audioPage === 1 ? (audioResp.data.data || []) : [...prev, ...(audioResp.data.data || [])]);
        }
      } catch (e:any) {
        if (!ignore) setError(e.message || 'Search failed');
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchData();
    return () => { ignore = true; };
  }, [q, nkPage, audioPage]);

  // Reset pages if query changes (avoid append from previous query)
  useEffect(() => {
    setNkPage(1);
    setAudioPage(1);
  }, [q]);

  const handlePlayAudio = (audio: AudioFile) => {
    // If this audio is already playing, just toggle play/pause
    if (state.currentAudio?.id === audio.id) {
      togglePlay();
    } else {
      playAudio(audio);
    }
  };

  const handleAddToQueue = (audio: AudioFile) => {
    const wasAdded = addToQueue(audio);
    if (wasAdded) {
      showToast(`Added "${audio.title}" to queue`, 'success', 3000, 'bottom-right');
    } else {
      showToast(`"${audio.title}" is already in queue`, 'info', 3000, 'bottom-right');
    }
  };

  const handleToggleFavorite = (audio: AudioFile) => {
    const wasAlreadyFavorite = isFavorite(audio.id);
    toggleFavorite(audio);
    if (wasAlreadyFavorite) {
      showToast(`Removed "${audio.title}" from favorites`, 'info', 3000, 'top-right-favorites');
    } else {
      showToast(`Added "${audio.title}" to favorites`, 'success', 3000, 'top-right-favorites');
    }
  };

  const handleDownloadComplete = (audio: AudioFile, success: boolean) => {
    if (success) {
      showToast(`Downloaded "${audio.title}" successfully! 🎵`, 'success', 4000, 'bottom-right');
    } else {
      showToast(`Failed to download "${audio.title}". Please try again.`, 'error', 4000, 'bottom-right');
    }
  };

  const highlight = (text: string) => {
    const queryText = q.trim();
    if (!queryText) return text;
    try {
      const escaped = queryText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})`, 'ig');
      return text.split(regex).map((part, i) => regex.test(part) ? <mark key={i}>{part}</mark> : part);
    } catch {
      return text;
    }
  };

  return (
    <div className="search-page">
      <SEO
        title={`Search Results for "${q}" - NaatCity`}
        description={`Search results for "${q}" on NaatCity. Found ${naatKhawans.length} Naat Khawans and ${audios.length} naats matching your search query.`}
        keywords={`search ${q}, ${q} naats, ${q} naat khawan, islamic search results`}
        url={`https://naatcity.com/search?q=${encodeURIComponent(q)}`}
      />
      <div className="sr-only" aria-live="polite">
        {(nkTotalCount + audioTotalCount) > 0 && `${nkTotalCount} naatkhawans and ${audioTotalCount} naats found`}
      </div>

      {/* Main Content */}
      <main className="search-main">
        <h1 className="visually-hidden">Search Results</h1>
        <div className="results-header">
          <p className="results-intro">
            Results for <strong>"{q}"</strong>
            {loading && <span className="searching-indicator"> – searching...</span>}
          </p>
          {!loading && !error && (nkTotalCount > 0 || audioTotalCount > 0) && (
            <div className="results-summary">
              <div className="results-count-display">
                <span className="count-icon">🔍</span>
                <div className="count-details">
                  <div className="total-count">
                    <strong>{nkTotalCount + audioTotalCount}</strong> results found
                  </div>
                  <div className="count-breakdown">
                    {nkTotalCount > 0 && (
                      <span className="count-item">
                        <Users size={14} /> {nkTotalCount} Naat Khawan{nkTotalCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {nkTotalCount > 0 && audioTotalCount > 0 && <span className="count-separator">•</span>}
                    {audioTotalCount > 0 && (
                      <span className="count-item">
                        <Music size={14} /> {audioTotalCount} Naat{audioTotalCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {error && <div className="error-msg">{error}</div>}
        {!loading && !error && q && naatKhawans.length === 0 && audios.length === 0 && (
          <div className="empty-state">
            <Search size={56} />
            <h2>No results found</h2>
            <p>Try different or shorter keywords.</p>
          </div>
        )}

        {naatKhawans.length > 0 && (
          <section className="results-section">
            <div className="section-header-inline">
              <h2><Users size={20}/> NaatKhawans <span className="count-badge">{nkTotalCount}</span></h2>
            </div>
            <div className="naatkhawans-grid condensed">
              {naatKhawans.map(nk => (
                <Link
                  key={nk.id}
                  to={`/naatkhawan/${nk.id}`}
                  className="naatkhawan-card with-bottom-bar"
                >
                  <div className="card-content with-bottom-bar">
                    <div className="card-body">
                      <h3 className="naatkhawan-name">{highlight(nk.name)}</h3>
                    </div>
                    <div className="naatkhawan-card-bottom">
                      <span className="naatkhawan-count">{nk.audioCount || 0} Naats</span>
                      {nk.urduName && (
                        <span className="naatkhawan-urdu-name inline" dir="rtl">{nk.urduName}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {nkPage < nkTotalPages && (
              <div className="load-more-wrapper">
                <button type="button" className="load-more-btn gradient" onClick={() => setNkPage(p => p + 1)} disabled={loading}>{loading ? 'Loading...' : 'Load More'}</button>
              </div>
            )}
          </section>
        )}

        {audios.length > 0 && (
          <section className="results-section">
            <div className="section-header-inline">
              <h2><Music size={20}/> Naats <span className="count-badge">{audioTotalCount}</span></h2>
            </div>
            <div className="naat-card-grid">
              {audios.map((audio, idx) => {
                const isPlaying = state.currentAudio?.id === audio.id && state.isPlaying;
                return (
                  <NaatCard
                    key={audio.id}
                    naat={audio}
                    serialNumber={idx + 1}
                    isPlaying={isPlaying}
                    isFavorite={isFavorite(audio.id)}
                    onPlay={handlePlayAudio}
                    onPause={() => togglePlay()}
                    onAddToQueue={handleAddToQueue}
                    onToggleFavorite={handleToggleFavorite}
                    onDownloadComplete={handleDownloadComplete}
                  />
                );
              })}
            </div>
            {audioPage < audioTotalPages && (
              <div className="load-more-wrapper">
                <button type="button" className="load-more-btn gradient" onClick={() => setAudioPage(p => p + 1)} disabled={loading}>{loading ? 'Loading...' : 'Load More'}</button>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default SearchResultsPage;
